import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppContext } from '../../../context/AppContext';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTasks } from '@fortawesome/free-solid-svg-icons';
import { TasksFeed } from '../../blocks/TasksFeed';
import { FilterToggle } from '../../helpers/FilterToggle';

const TASK_TABS = {
    mine: 'mine',
    all: 'all',
};

const isAssignedToUser = (task, user) => {
    if (!task.assigned_to || !user) return false;
    const assigned = task.assigned_to.toLowerCase();
    if (user.email) {
        return assigned.includes(user.email.toLowerCase());
    }
    const firstName = user.first_name?.toLowerCase();
    const lastName = user.last_name?.toLowerCase();
    return Boolean(
        (firstName && assigned.includes(firstName)) ||
        (lastName && assigned.includes(lastName))
    );
};

const sortTasksByDueDate = (tasks) => {
    return [...tasks].sort((a, b) => {
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date) - new Date(b.due_date);
    });
};

export const MyTasksPage = () => {
    const { projects, user, isLoadingProjects } = useAppContext();
    const [searchParams, setSearchParams] = useSearchParams();
    const [showCompleted, setShowCompleted] = useState(false);
    const [projectFilter, setProjectFilter] = useState('all');
    const [userFilter, setUserFilter] = useState('all');

    const tabFromUrl = searchParams.get('tab');
    const activeTab =
        tabFromUrl === TASK_TABS.all ? TASK_TABS.all : TASK_TABS.mine;

    const setActiveTab = useCallback(
        (tab) => {
            setSearchParams({ tab }, { replace: true });
        },
        [setSearchParams]
    );

    useEffect(() => {
        if (!tabFromUrl) {
            setSearchParams({ tab: TASK_TABS.mine }, { replace: true });
            return;
        }
        if (tabFromUrl !== TASK_TABS.mine && tabFromUrl !== TASK_TABS.all) {
            setSearchParams({ tab: TASK_TABS.mine }, { replace: true });
        }
    }, [tabFromUrl, setSearchParams]);

    const allProjectTasks = useMemo(() => {
        if (!projects.length) return [];

        const tasks = [];
        projects.forEach((project) => {
            if (!project.project_tasks || !Array.isArray(project.project_tasks)) return;
            project.project_tasks.forEach((task) => {
                tasks.push({
                    ...task,
                    project_id: project.id,
                    project_name: project.project_name || project.name || `Project ${project.id}`,
                    project,
                });
            });
        });
        return tasks;
    }, [projects]);

    const myTasks = useMemo(() => {
        if (!user) return [];
        let filtered = allProjectTasks.filter((task) => isAssignedToUser(task, user));
        if (!showCompleted) {
            filtered = filtered.filter((task) => task.status !== 'Completed');
        }
        return sortTasksByDueDate(filtered);
    }, [allProjectTasks, user, showCompleted]);

    const allTasksBase = useMemo(() => {
        let filtered = showCompleted
            ? allProjectTasks
            : allProjectTasks.filter((task) => task.status !== 'Completed');
        return sortTasksByDueDate(filtered);
    }, [allProjectTasks, showCompleted]);

    const projectFilterOptions = useMemo(() => {
        const byId = new Map();
        allTasksBase.forEach((task) => {
            if (task.project_id == null || byId.has(task.project_id)) return;
            byId.set(task.project_id, {
                id: task.project_id,
                name: task.project_name || `Project ${task.project_id}`,
            });
        });
        return Array.from(byId.values()).sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        );
    }, [allTasksBase]);

    const userFilterOptions = useMemo(() => {
        const assignees = new Set();
        allTasksBase.forEach((task) => {
            if (task.assigned_to) assignees.add(task.assigned_to);
        });
        return Array.from(assignees).sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: 'base' })
        );
    }, [allTasksBase]);

    const allTasks = useMemo(() => {
        let filtered = allTasksBase;

        if (projectFilter !== 'all') {
            filtered = filtered.filter(
                (task) => String(task.project_id) === String(projectFilter)
            );
        }

        if (userFilter !== 'all') {
            filtered = filtered.filter((task) => task.assigned_to === userFilter);
        }

        return filtered;
    }, [allTasksBase, projectFilter, userFilter]);

    const hasActiveFilters = projectFilter !== 'all' || userFilter !== 'all';

    const clearFilters = () => {
        setProjectFilter('all');
        setUserFilter('all');
    };

    const visibleTasks = activeTab === TASK_TABS.all ? allTasks : myTasks;

    const taskTabs = [
        { id: TASK_TABS.mine, label: 'My Tasks', count: myTasks.length },
        { id: TASK_TABS.all, label: 'All Tasks', count: allTasksBase.length },
    ];

    const emptyMessage = (() => {
        if (activeTab === TASK_TABS.all) {
            if (hasActiveFilters) {
                return 'No tasks match the selected filters.';
            }
            return showCompleted
                ? 'There are no tasks across projects yet.'
                : 'There are no pending tasks across projects.';
        }
        return showCompleted
            ? "You don't have any tasks assigned to you yet."
            : "You don't have any pending tasks. Great job!";
    })();

    if (isLoadingProjects) {
        return (
            <div className="page">
                <div className="main-content">
                    <div className="page-header">
                        <h1><FontAwesomeIcon icon={faTasks} /> Tasks</h1>
                        <hr />
                    </div>
                    <p>Loading tasks...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page">
            <div className="main-content">
                <div className="page-header">
                    <h1><FontAwesomeIcon icon={faTasks} /> Tasks</h1>
                    <hr />
                </div>

                <div className="admin-tabs admin-tabs--compact">
                    <div className="admin-tabs__list">
                        {taskTabs.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={`admin-tab${activeTab === tab.id ? ' admin-tab--active' : ''}`}
                            >
                                {tab.label}
                                <span className={`admin-tab__count${activeTab === tab.id ? ' admin-tab__count--active' : ''}`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="my-tasks-controls">
                    <FilterToggle
                        label="Include completed"
                        checked={showCompleted}
                        onChange={setShowCompleted}
                    />
                    <div className="tasks-summary">
                        {activeTab === TASK_TABS.all && hasActiveFilters ? (
                            <span>
                                Showing {visibleTasks.length} of {allTasksBase.length} task
                                {allTasksBase.length !== 1 ? 's' : ''}
                            </span>
                        ) : showCompleted ? (
                            <span>Showing all {visibleTasks.length} task{visibleTasks.length !== 1 ? 's' : ''}</span>
                        ) : (
                            <span>Showing {visibleTasks.length} pending task{visibleTasks.length !== 1 ? 's' : ''}</span>
                        )}
                    </div>
                </div>

                {activeTab === TASK_TABS.all && (
                    <div className="admin-tools admin-tools--compact">
                        <h3>Search Tools</h3>
                        <div className="admin-tools-grid">
                            <div className="form-group">
                                <label htmlFor="taskProjectFilter" className="form-label">
                                    Filter by Project
                                </label>
                                <select
                                    id="taskProjectFilter"
                                    value={projectFilter}
                                    onChange={(e) => setProjectFilter(e.target.value)}
                                    className="form-select"
                                >
                                    <option value="all">All Projects</option>
                                    {projectFilterOptions.map((project) => (
                                        <option key={project.id} value={String(project.id)}>
                                            {project.name} (#{project.id})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="taskUserFilter" className="form-label">
                                    Filter by User
                                </label>
                                <select
                                    id="taskUserFilter"
                                    value={userFilter}
                                    onChange={(e) => setUserFilter(e.target.value)}
                                    className="form-select"
                                >
                                    <option value="all">All Users</option>
                                    {userFilterOptions.map((assignee) => (
                                        <option key={assignee} value={assignee}>
                                            {assignee}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <button
                                    type="button"
                                    onClick={clearFilters}
                                    className="btn btn-secondary btn--block"
                                    disabled={!hasActiveFilters}
                                >
                                    Clear Filters
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <TasksFeed
                    tasks={visibleTasks}
                    showProjectLink
                    showAssignee
                    emptyMessage={emptyMessage}
                />
            </div>
        </div>
    );
};
